import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { MdBook } from "react-icons/md";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { NotifyContext } from "../../context/notifyContext";
import CourseService from "../../services/api/CourseService";
import {
  Course,
  CourseAnalytics,
  CreateExercisePayload,
  CreateQuestionPayload,
  EducationalLevel,
  EducationalSystem,
  Exercise,
  Lesson,
  Unit,
} from "../../Types/Courses";
import CourseBuilderLayout from "./components/CourseBuilderLayout";
import CourseInformationPanel from "./components/CourseInformationPanel";
import CourseStructurePanel from "./components/CourseStructurePanel";
import LessonEditorPanel from "./components/LessonEditorPanel";
import CreateExerciseModal from "./components/CreateExerciseModal";
import { LessonFormState, defaultLessonForm } from "./components/courseBuilder.types";

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);

  const [savingCourse, setSavingCourse] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [loading, setLoading] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const [courseStats, setCourseStats] = useState<CourseAnalytics | null>(null);
  const [systems, setSystems] = useState<EducationalSystem[]>([]);
  const [levels, setLevels] = useState<EducationalLevel[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exerciseSearch, setExerciseSearch] = useState("");
  const [exercisePage, setExercisePage] = useState(1);
  const [exerciseLastPage, setExerciseLastPage] = useState(1);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [showCreateExerciseModal, setShowCreateExerciseModal] = useState(false);
  const [creatingExerciseInline, setCreatingExerciseInline] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [lessonForm, setLessonForm] = useState<LessonFormState>(defaultLessonForm);
  const [newUnitTitle, setNewUnitTitle] = useState("");
  const [newLessonTitle, setNewLessonTitle] = useState("");

  useBreadcrumbs([
    { label: "Tableau de bord", is_link: true, href: "/app/dashboard" },
    { label: "Cours", icon: <MdBook />, is_link: true, href: "/app/courses" },
    { label: id ? `Édition #${id}` : "Nouveau", is_link: false },
  ]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: course?.title || "",
      slug: course?.slug || "",
      description: course?.description || "",
      category: course?.category || "",
      educational_system_id: course?.educational_system_id || "",
      level_id: course?.level_id || "",
      order: course?.order ?? 0,
      is_published: course?.is_published ?? false,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required("Le titre est obligatoire"),
      educational_system_id: Yup.number().required("Le système éducatif est obligatoire"),
    }),
    onSubmit: async (values) => {
      setSavingCourse(true);
      try {
        const payload = {
          title: values.title,
          slug: values.slug || null,
          description: values.description || null,
          category: values.category || null,
          educational_system_id: Number(values.educational_system_id),
          level_id: values.level_id ? Number(values.level_id) : null,
          order: Number(values.order) || 0,
          is_published: values.is_published,
        };

        if (id) {
          await CourseService.updateCourse(Number(id), payload);
          notify("Cours mis à jour");
          await loadCourse();
        } else {
          const response: any = await CourseService.createCourse(payload);
          notify("Cours créé");
          const created = response?.data?.course;
          if (created?.id) navigate(`/app/courses/${created.id}/edit`);
        }
      } catch (error: any) {
        notify(error?.message || error || "Erreur lors de l'enregistrement", "error");
      } finally {
        setSavingCourse(false);
      }
    },
  });

  const selectedSystemId = Number(formik.values.educational_system_id) || 0;
  const filteredLevels = useMemo(
    () => levels.filter((item) => item.educational_system_id === selectedSystemId),
    [levels, selectedSystemId]
  );

  const selectedUnit = useMemo(
    () => course?.units?.find((item) => item.id === selectedUnitId) || null,
    [course, selectedUnitId]
  );

  const loadEducationParams = useCallback(async () => {
    const response: any = await CourseService.getEducationParams();
    const systemsData = response?.data?.systems || [];
    setSystems(systemsData);
    setLevels(systemsData.flatMap((system: EducationalSystem) => system.levels || []));
  }, []);

  const loadExercises = useCallback(
    async (searchWord: string, page = 1, append = false) => {
      setLoadingExercises(true);
      try {
        const response: any = await CourseService.getExercises({
          page,
          per_page: 20,
          search_word: searchWord || undefined,
        });
        const payload = response?.data || {};
        const nextItems: Exercise[] = payload?.exercises || [];
        setExercises((previous) => {
          if (!append) return nextItems;
          const merged = [...previous];
          nextItems.forEach((exercise) => {
            if (!merged.some((item) => item.id === exercise.id)) {
              merged.push(exercise);
            }
          });
          return merged;
        });
        setExercisePage(page);
        setExerciseLastPage(payload?.last_page || 1);
      } catch (error: any) {
        notify(error?.message || error || "Impossible de charger les exercices", "error");
      } finally {
        setLoadingExercises(false);
      }
    },
    [notify]
  );

  const loadCourse = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response: any = await CourseService.getCourseAnalytics(Number(id));
      const loadedCourse = response?.data?.course || null;
      setCourse(loadedCourse);
      setCourseStats(response?.data?.stats || null);
      if (loadedCourse?.units?.length) {
        setSelectedUnitId((previous) => {
          const selectedStillExists = loadedCourse.units.some((item: Unit) => item.id === previous);
          if (!previous || !selectedStillExists) {
            return loadedCourse.units[0].id;
          }
          return previous;
        });
      }
    } catch (error: any) {
      notify(error?.message || error || "Impossible de charger le cours", "error");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    loadEducationParams().catch((error) => {
      notify(error?.message || error || "Impossible de charger les systèmes éducatifs", "error");
    });
  }, [loadEducationParams, notify]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadExercises(exerciseSearch, 1, false);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [exerciseSearch, loadExercises]);

  useEffect(() => {
    if (!id) return;
    loadCourse();
  }, [id, loadCourse]);

  useEffect(() => {
    if (!selectedLesson) {
      setLessonForm(defaultLessonForm);
      return;
    }
    setLessonForm({
      id: selectedLesson.id,
      title: selectedLesson.title,
      type: selectedLesson.type,
      content: selectedLesson.content || "",
      video_url: selectedLesson.video_url || "",
      duration_minutes: selectedLesson.duration_minutes || "",
      exercise_id: selectedLesson.exercise_id || "",
    });
  }, [selectedLesson]);

  const ensureCourseId = () => {
    if (!id) {
      notify("Enregistre d'abord le cours avant de gérer les unités/leçons", "warning");
      return null;
    }
    return Number(id);
  };

  const createUnit = async () => {
    const courseId = ensureCourseId();
    if (!courseId) return;
    if (!newUnitTitle.trim()) {
      notify("Le nom de l'unité est obligatoire", "error");
      return;
    }
    try {
      await CourseService.createUnit(courseId, { title: newUnitTitle.trim() });
      notify("Unité créée");
      setNewUnitTitle("");
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la création de l'unité", "error");
    }
  };

  const renameUnit = async (unit: Unit) => {
    const courseId = ensureCourseId();
    if (!courseId) return;
    const title = window.prompt("Nouveau nom de l'unité", unit.title);
    if (!title) return;
    try {
      await CourseService.updateUnit(courseId, unit.id, { title: title.trim() });
      notify("Unité mise à jour");
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la mise à jour", "error");
    }
  };

  const deleteUnit = async (unit: Unit) => {
    const courseId = ensureCourseId();
    if (!courseId) return;
    if (!window.confirm(`Supprimer l'unité "${unit.title}" ?`)) return;
    try {
      await CourseService.deleteUnit(courseId, unit.id);
      if (selectedUnitId === unit.id) {
        setSelectedUnitId(null);
        setSelectedLesson(null);
      }
      notify("Unité supprimée");
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la suppression", "error");
    }
  };

  const reorderUnit = async (direction: "up" | "down", unit: Unit) => {
    const courseId = ensureCourseId();
    if (!courseId || !course?.units?.length) return;
    const units = [...course.units];
    const index = units.findIndex((item) => item.id === unit.id);
    if ((direction === "up" && index === 0) || (direction === "down" && index === units.length - 1)) {
      return;
    }
    const nextIndex = direction === "up" ? index - 1 : index + 1;
    [units[index], units[nextIndex]] = [units[nextIndex], units[index]];
    try {
      await CourseService.reorderUnits(
        courseId,
        units.map((item) => item.id)
      );
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors du réordonnancement", "error");
    }
  };

  const createLesson = async () => {
    const courseId = ensureCourseId();
    if (!courseId || !selectedUnitId) return;
    if (!newLessonTitle.trim()) {
      notify("Le titre de la leçon est obligatoire", "error");
      return;
    }
    try {
      await CourseService.createLesson(courseId, selectedUnitId, {
        title: newLessonTitle.trim(),
        type: "text",
        content: "",
      });
      notify("Leçon créée");
      setNewLessonTitle("");
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la création de la leçon", "error");
    }
  };

  const deleteLesson = async (lesson: Lesson) => {
    const courseId = ensureCourseId();
    if (!courseId || !selectedUnitId) return;
    if (!window.confirm(`Supprimer la leçon "${lesson.title}" ?`)) return;
    try {
      await CourseService.deleteLesson(courseId, selectedUnitId, lesson.id);
      if (selectedLesson?.id === lesson.id) setSelectedLesson(null);
      notify("Leçon supprimée");
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de la suppression", "error");
    }
  };

  const reorderLesson = async (direction: "up" | "down", lesson: Lesson) => {
    const courseId = ensureCourseId();
    if (!courseId || !selectedUnit?.lessons?.length || !selectedUnitId) return;
    const lessons = [...selectedUnit.lessons];
    const index = lessons.findIndex((item) => item.id === lesson.id);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === lessons.length - 1)
    ) {
      return;
    }
    const nextIndex = direction === "up" ? index - 1 : index + 1;
    [lessons[index], lessons[nextIndex]] = [lessons[nextIndex], lessons[index]];
    try {
      await CourseService.reorderLessons(
        courseId,
        selectedUnitId,
        lessons.map((item) => item.id)
      );
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors du réordonnancement", "error");
    }
  };

  const saveLesson = async () => {
    const courseId = ensureCourseId();
    if (!courseId || !selectedUnitId) return;
    if (!lessonForm.title.trim()) {
      notify("Le titre de la leçon est obligatoire", "error");
      return;
    }
    if (lessonForm.type === "exercise" && !lessonForm.exercise_id) {
      notify("Sélectionne un exercice pour ce type de leçon", "error");
      return;
    }

    setSavingLesson(true);
    try {
      const isExercise = lessonForm.type === "exercise";
      const hasVideo = lessonForm.type === "video" || lessonForm.type === "mixed";
      const hasContent = lessonForm.type === "text" || lessonForm.type === "mixed";
      const payload = {
        title: lessonForm.title.trim(),
        type: lessonForm.type,
        content: hasContent ? lessonForm.content || null : null,
        video_url: hasVideo ? lessonForm.video_url || null : null,
        duration_minutes: lessonForm.duration_minutes
          ? Number(lessonForm.duration_minutes)
          : null,
        exercise_id: isExercise ? Number(lessonForm.exercise_id) : null,
      };

      if (lessonForm.id) {
        await CourseService.updateLesson(courseId, selectedUnitId, lessonForm.id, payload);
        notify("Leçon mise à jour");
      } else {
        await CourseService.createLesson(courseId, selectedUnitId, payload);
        notify("Leçon créée");
      }

      setSelectedLesson(null);
      setLessonForm(defaultLessonForm);
      await loadCourse();
    } catch (error: any) {
      notify(error?.message || error || "Erreur lors de l'enregistrement", "error");
    } finally {
      setSavingLesson(false);
    }
  };

  const resetLessonEditor = () => {
    setSelectedLesson(null);
    setLessonForm(defaultLessonForm);
  };

  const canLoadMoreExercises = exercisePage < exerciseLastPage;

  const handleCreateExerciseWithQuestions = async (
    payload: CreateExercisePayload,
    questions: CreateQuestionPayload[]
  ) => {
    setCreatingExerciseInline(true);
    try {
      const response: any = await CourseService.createExercise(payload);
      const created: Exercise | undefined = response?.data?.exercise;
      if (!created?.id) {
        throw new Error("Création de l'exercice impossible.");
      }

      for (let index = 0; index < questions.length; index += 1) {
        await CourseService.createQuestion(created.id, {
          ...questions[index],
          order: index + 1,
        });
      }

      setExercises((previous) => [created, ...previous.filter((item) => item.id !== created.id)]);
      setLessonForm((previous) => ({
        ...previous,
        type: "exercise",
        exercise_id: created.id,
      }));
      setShowCreateExerciseModal(false);
      notify("Exercice créé avec ses questions et sélectionné");
    } catch (error: any) {
      notify(error?.message || error || "Impossible de créer l'exercice", "error");
      throw error;
    } finally {
      setCreatingExerciseInline(false);
    }
  };

  return (
    <>
      <CourseBuilderLayout
        courseInfo={
          <CourseInformationPanel
            title={formik.values.title}
            slug={formik.values.slug}
            category={formik.values.category}
            order={formik.values.order}
            description={formik.values.description}
            educationalSystemId={formik.values.educational_system_id}
            levelId={formik.values.level_id}
            isPublished={formik.values.is_published}
            systems={systems}
            levels={filteredLevels}
            titleError={
              formik.touched.title && formik.errors.title ? String(formik.errors.title) : undefined
            }
            educationalSystemError={
              formik.touched.educational_system_id && formik.errors.educational_system_id
                ? String(formik.errors.educational_system_id)
                : undefined
            }
            saving={savingCourse}
            submitLabel={id ? "Mettre à jour le cours" : "Créer le cours"}
            onTitleChange={(value) => formik.setFieldValue("title", value)}
            onSlugChange={(value) => formik.setFieldValue("slug", value)}
            onCategoryChange={(value) => formik.setFieldValue("category", value)}
            onOrderChange={(value) => formik.setFieldValue("order", value)}
            onDescriptionChange={(value) => formik.setFieldValue("description", value)}
            onEducationalSystemChange={(value) => {
              formik.setFieldValue("educational_system_id", value);
              formik.setFieldValue("level_id", "");
            }}
            onLevelChange={(value) => formik.setFieldValue("level_id", value)}
            onPublishedChange={(value) => formik.setFieldValue("is_published", value)}
            onSubmit={() => formik.handleSubmit()}
          />
        }
        structure={
          <CourseStructurePanel
            id={id}
            loading={loading}
            course={course}
            courseStats={courseStats}
            selectedUnitId={selectedUnitId}
            selectedLessonId={selectedLesson?.id}
            newUnitTitle={newUnitTitle}
            newLessonTitle={newLessonTitle}
            onNewUnitTitleChange={setNewUnitTitle}
            onNewLessonTitleChange={setNewLessonTitle}
            onCreateUnit={createUnit}
            onCreateLesson={createLesson}
            onSelectUnit={setSelectedUnitId}
            onSelectLesson={(unitId, lesson) => {
              setSelectedUnitId(unitId);
              setSelectedLesson(lesson);
            }}
            onGoToCourseDetails={() => navigate(`/app/courses/${id}/details`)}
            onReorderUnit={reorderUnit}
            onRenameUnit={renameUnit}
            onDeleteUnit={deleteUnit}
            onReorderLesson={reorderLesson}
            onDeleteLesson={deleteLesson}
          />
        }
        lessonEditor={
          <LessonEditorPanel
            selectedUnitId={selectedUnitId}
            lessonForm={lessonForm}
            savingLesson={savingLesson}
            exercises={exercises}
            loadingExercises={loadingExercises}
            canLoadMoreExercises={canLoadMoreExercises}
            onLessonFormChange={(patch) => setLessonForm((previous) => ({ ...previous, ...patch }))}
            onSearchExercise={setExerciseSearch}
            onLoadMoreExercises={() => {
              if (!canLoadMoreExercises || loadingExercises) return;
              loadExercises(exerciseSearch, exercisePage + 1, true);
            }}
            onOpenCreateExerciseModal={() => setShowCreateExerciseModal(true)}
            onGoToExerciseDetails={() =>
              lessonForm.exercise_id
                ? navigate(`/app/courses/exercises/${lessonForm.exercise_id}/details`)
                : undefined
            }
            onReset={resetLessonEditor}
            onCancel={resetLessonEditor}
            onSave={saveLesson}
          />
        }
      />

      <CreateExerciseModal
        open={showCreateExerciseModal}
        loading={creatingExerciseInline}
        systems={systems}
        levels={levels}
        defaultSystemId={formik.values.educational_system_id}
        defaultLevelId={formik.values.level_id}
        onClose={() => setShowCreateExerciseModal(false)}
        onSubmit={handleCreateExerciseWithQuestions}
      />
    </>
  );
}
